import { AxiosError } from 'axios';
import clsx from 'clsx';
import { useEffect } from 'react';
import { X } from 'react-feather';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { isIBAN } from 'validator';
import { HttpExceptionDto } from '../interfaces/http-exception.dto';
import { Organization } from '../interfaces/organization';
import { OrganizationClient } from '../interfaces/organization-client';
import { OrganizationClientUpdateDto } from '../interfaces/organization-client-update.dto';
import { OrganizationClientDto } from '../interfaces/organization-client.dto';
import { api } from '../services/api';
import myToastr from '../services/toastr';
import { useAppSelector } from '../store/hooks';
import { RootState } from '../store/store';

interface Props {
  closeModal: (organizationClient: OrganizationClient | null) => void;
  show: boolean;
  organizationClient: OrganizationClient | null;
}

const initialValues: OrganizationClientDto = {
  name: '',
  cif: '',
  businessName: '',
  address: '',
  phone: '',
  email: '',
  url: '',
  postalCode: '',
  city: '',
  province: '',
  country: '',
  organizationId: -1,
  bankAccountNumber: null,
  sepaBic: null,
  installmentCount: 1,
  installmentIntervalDays: null,
  equivalenceSurcharge: false,
};

const OrganizationClientModal = ({ closeModal, show, organizationClient }: Props) => {
  const organization: Organization = useAppSelector((state: RootState) => state.auth.organization!);
  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (show && organizationClient) {
      reset({
        name: organizationClient.organization.name,
        cif: organizationClient.organization.cif,
        businessName: organizationClient.organization.businessName,
        address: organizationClient.organization.address,
        phone: organizationClient.organization.phone,
        email: organizationClient.organization.email,
        url: organizationClient.organization.url,
        postalCode: organizationClient.organization.postalCode,
        city: organizationClient.organization.city,
        province: organizationClient.organization.province,
        country: organizationClient.organization.country,
        organizationId: organizationClient.organizationId,
        bankAccountNumber: organizationClient.bankAccountNumber,
        sepaBic: organizationClient.sepaBic,
        installmentCount: organizationClient.installmentCount,
        installmentIntervalDays: organizationClient.installmentIntervalDays,
        equivalenceSurcharge: organizationClient.equivalenceSurcharge,
      });
    } else if (show && !organizationClient) {
      reset(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const close = () => {
    closeModal(null);
  };

  const onSubmit = async (organizationClientDto: OrganizationClientDto) => {
    if (!isDirty) {
      closeModal(null);
      return;
    }
    if (organizationClient) {
      try {
        const organizationClientUpdateDto: OrganizationClientUpdateDto = {
          bankAccountNumber: organizationClientDto.bankAccountNumber ? organizationClientDto.bankAccountNumber.replace(/\s+/g, '') : null,
          sepaBic: organizationClientDto.sepaBic || null,
          installmentCount: !organizationClientDto.installmentCount ? null : organizationClientDto.installmentCount,
          installmentIntervalDays: !organizationClientDto.installmentIntervalDays ? null : organizationClientDto.installmentIntervalDays,
          equivalenceSurcharge: organizationClientDto.equivalenceSurcharge,
        };
        await api.updateOrganizationClient(organizationClient.id, organizationClientUpdateDto);
        closeModal(organizationClient);
      } catch (e: any) {
        const axiosError: AxiosError<HttpExceptionDto> = e;
        if (axiosError.response?.data) {
          const httpExceptionDto: HttpExceptionDto = axiosError.response.data;
          myToastr.error(httpExceptionDto.message);
        }
      }
    } else {
      try {
        organizationClientDto.organizationId = organization.id;
        organizationClientDto.installmentCount = !organizationClientDto.installmentCount ? null : organizationClientDto.installmentCount;
        organizationClientDto.installmentIntervalDays = !organizationClientDto.installmentIntervalDays ? null : organizationClientDto.installmentIntervalDays;
        organizationClientDto.bankAccountNumber = organizationClientDto.bankAccountNumber ? organizationClientDto.bankAccountNumber.replace(/\s+/g, '') : null;
        organizationClientDto.sepaBic = organizationClientDto.sepaBic || null;
        const newOrganizationClient: OrganizationClient = await api.createOrganizationClient(organizationClientDto);
        closeModal(newOrganizationClient);
      } catch (e: any) {
        const axiosError: AxiosError<HttpExceptionDto> = e;
        if (axiosError.response?.data) {
          const httpExceptionDto: HttpExceptionDto = axiosError.response.data;
          myToastr.error(httpExceptionDto.message);
        }
      }
    }
  };

  return (
    <Modal className="vercomi-modal my-form organization-modal" isOpen={show} onRequestClose={close} shouldCloseOnOverlayClick={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="content">
          <div className="d-flex justify-content-between mb-3">
            <div className="title">{organizationClient ? 'Editar cliente' : 'Crear cliente'}</div>
            <button type="button" className="close-button-modal" onClick={close} title="Cerrar">
              <X size={16} />
            </button>
          </div>
          <div className="row my-3">
            <div className="col">
              <div className={'input-name ' + (errors?.name ? 'error' : '')}>Nombre *</div>
              <input type="text" {...register('name', { required: true })} className={clsx({ error: errors?.name })} placeholder="Nombre" disabled={Boolean(organizationClient)} />
              {errors.name && <div className="error-message">{errors.name.message}</div>}
            </div>
            <div className="col">
              <div className={clsx('input-name', { error: errors?.businessName })}>Razón Social *</div>
              <input
                type="text"
                {...register('businessName', { required: true })}
                placeholder="Razón social"
                className={clsx({ error: errors?.businessName })}
                disabled={Boolean(organizationClient)}
              />
              {errors.businessName && <div className="error-message">{errors.businessName.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col">
              <div className={clsx('input-name', { error: errors?.phone })}>Teléfono *</div>
              <input type="number" {...register('phone', { required: true })} placeholder="Teléfono" className={clsx({ error: errors?.phone })} disabled={Boolean(organizationClient)} />
              {errors.phone && <div className="error-message">{errors.phone.message}</div>}
            </div>
            <div className="col">
              <div className={clsx('input-name', { error: errors?.cif })}>CIF *</div>
              <input type="text" {...register('cif', { required: true })} className={clsx({ error: errors?.cif })} placeholder="CIF" disabled={Boolean(organizationClient)} />
              {errors.cif && <div className="error-message">{errors.cif.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col">
              <div className={clsx('input-name', { error: errors?.address })}>Dirección *</div>
              <input type="text" {...register('address', { required: true })} className={clsx({ error: errors?.address })} placeholder="Dirección" disabled={Boolean(organizationClient)} />
              {errors.address && <div className="error-message">{errors.address.message}</div>}
            </div>
            <div className="col">
              <div className={clsx('input-name', { error: errors?.email })}>Email *</div>
              <input type="email" {...register('email', { required: true })} className={clsx({ error: errors?.email })} placeholder="Email" disabled={Boolean(organizationClient)} />
              {errors.email && <div className="error-message">{errors.email.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.postalCode })}>Código postal *</div>
              <input type="text" {...register('postalCode', { required: true })} className={clsx({ error: errors?.postalCode })} placeholder="Código postal" disabled={Boolean(organizationClient)} />
              {errors.postalCode && <div className="error-message">{errors.postalCode.message}</div>}
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.city })}>Ciudad *</div>
              <input type="text" {...register('city', { required: true })} className={clsx({ error: errors?.city })} placeholder="Ciudad" disabled={Boolean(organizationClient)} />
              {errors.city && <div className="error-message">{errors.city.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.province })}>Provincia *</div>
              <input type="text" {...register('province', { required: true })} className={clsx({ error: errors?.province })} placeholder="Provincia" disabled={Boolean(organizationClient)} />
              {errors.province && <div className="error-message">{errors.province.message}</div>}
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.country })}>País *</div>
              <input type="text" {...register('country', { required: true })} className={clsx({ error: errors?.country })} placeholder="País" disabled={Boolean(organizationClient)} />
              {errors.country && <div className="error-message">{errors.country.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.url })}>Url</div>
              <input type="text" {...register('url')} className={clsx({ error: errors?.url })} placeholder="Url" disabled={Boolean(organizationClient)} />
              {errors.url && <div className="error-message">{errors.url.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.bankAccountNumber })}>Número de cuenta bancaria</div>
              <input
                type="text"
                {...register('bankAccountNumber', {
                  validate: (value) => {
                    if (!value) {
                      return true;
                    }
                    return isIBAN(value) || 'Número de cuenta bancaria no válido';
                  },
                })}
                className={clsx({ error: errors?.bankAccountNumber })}
                placeholder="Número de cuenta bancaria"
              />
              {errors.bankAccountNumber && <div className="error-message">{errors.bankAccountNumber.message}</div>}
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.sepaBic })}>BIC del banco del acreedor para SEPA</div>
              <input type="text" {...register('sepaBic')} className={clsx({ error: errors?.sepaBic })} placeholder="Número BIC" />
              {errors.sepaBic && <div className="error-message">{errors.sepaBic.message}</div>}
            </div>
          </div>
          <div className="row my-3">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.installmentCount })}>Forma de pago facturas</div>
              <input
                type="number"
                {...register('installmentCount', {
                  valueAsNumber: true,
                  min: {
                    value: 1,
                    message: 'El número de plazos debe ser al menos 1',
                  },
                })}
                className={clsx({ error: errors?.installmentCount })}
                min={1}
                placeholder="Número de plazos para pago de facturas"
                step={1}
              />
              {errors.installmentCount && <div className="error-message">{errors.installmentCount.message}</div>}
            </div>
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.installmentIntervalDays })}>Días entre pagos</div>
              <input
                type="number"
                {...register('installmentIntervalDays', {
                  valueAsNumber: true,
                  min: {
                    value: 1,
                    message: 'Los días entre pagos deben ser al menos 1',
                  },
                })}
                className={clsx({ error: errors?.installmentIntervalDays })}
                min={1}
                placeholder="Días entre pagos"
                step={1}
              />
              {errors.installmentIntervalDays && <div className="error-message">{errors.installmentIntervalDays.message}</div>}
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <div className={clsx('input-name', { error: errors?.equivalenceSurcharge })}>Aplicar recargo de equivalencia</div>
              <input type="checkbox" className="form-check-input" {...register('equivalenceSurcharge')} />
              {errors.equivalenceSurcharge && <div className="error-message">{errors.equivalenceSurcharge.message}</div>}
            </div>
          </div>
        </div>
        <button className={`save-button`} type="submit">
          Guardar
        </button>
      </form>
    </Modal>
  );
};

export default OrganizationClientModal;
